import type { WorkspaceOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, toast } from "@sagot/ui";
import { useState } from "react";
import { FormAlert } from "../auth/components/form-alert";
import { useSources } from "../features/deploy-knowledge/knowledge/use-knowledge";
import { useUploads } from "../features/deploy-knowledge/knowledge/use-uploads";
import { useSourceEvents } from "../features/deploy-knowledge/shared/use-source-events";
import { useErrorMessage } from "../lib/api-errors";
import { WorkspaceScope } from "../session/workspace-context";
import { CrawlTab } from "./knowledge/crawl-tab";
import { FilesTab } from "./knowledge/files-tab";
import { QaTab } from "./knowledge/qa-tab";
import { draftsFromProgress, progressFromDrafts, reviewDrafts, withBlankRow, type QaDraft } from "./knowledge/qa-model";
import { SourcesList, type SourceTab } from "./knowledge/sources-list";
import { useQaSave } from "./knowledge/use-qa-save";
import { useSourceTargets } from "./knowledge/use-source-targets";
import { useSetupCopy } from "./use-catalog";
import { readOnboarding, useSaveProgress } from "./use-onboarding";

const K = "onboarding.knowledge";
const KB = "deployKnowledge.knowledge";
const STEP_AFTER_KNOWLEDGE = 3;

interface StepKnowledgeProps {
  workspace: WorkspaceOut | undefined;
  onBack: () => void;
  onNext: () => void;
}

function KnowledgeBody({ workspace, onBack, onNext }: StepKnowledgeProps) {
  const { t } = useTranslation();
  const copy = useSetupCopy(workspace);
  const save = useSaveProgress(workspace);
  const message = useErrorMessage();
  const targets = useSourceTargets(workspace);
  const events = useSourceEvents();
  const sources = useSources(events);
  const uploads = useUploads({ storageFailed: t(`${KB}.add.storageFailed`), infected: t(`${KB}.add.infected`) });
  const saveQa = useQaSave(targets);
  const [tab, setTab] = useState<SourceTab>("files");
  const [drafts, setDrafts] = useState<QaDraft[]>(() => draftsFromProgress(readOnboarding(workspace).data.qa));
  const [problems, setProblems] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [savingQa, setSavingQa] = useState(false);
  const [leaving, setLeaving] = useState(false);

  /** Turns complete Q&A pairs into sources. Returns the rows to remember, or null to stay on the step. */
  const persistQa = async (): Promise<QaDraft[] | null> => {
    const review = reviewDrafts(drafts);
    if (review.incomplete.length > 0) {
      setProblems(new Set(review.incomplete.map((draft) => draft.id)));
      setError(t(`${K}.qaIncomplete`));
      setTab("qa");
      return null;
    }
    setProblems(new Set());
    if (review.pending.length === 0) return drafts;
    const outcome = await saveQa(drafts, review.pending);
    setDrafts(withBlankRow(outcome.drafts));
    if (outcome.saved > 0) toast.success(t(`${K}.qaSaved`, { count: outcome.saved }));
    if (outcome.failed.length === 0) return outcome.drafts;
    setProblems(new Set(outcome.failed.map((draft) => draft.id)));
    setError(t(`${K}.qaPartial`, { failed: outcome.failed.length, total: review.pending.length, reason: outcome.reason ?? "" }));
    setTab("qa");
    await save(2, { qa: progressFromDrafts(outcome.drafts) });
    return null;
  };

  const run = async (finish: (rows: QaDraft[]) => Promise<void>, busy: (on: boolean) => void) => {
    setError(null);
    busy(true);
    try {
      const rows = await persistQa();
      if (rows) await finish(rows);
    } catch (caught) {
      setError(message(caught));
    } finally {
      busy(false);
    }
  };
  const saveOnly = () => run(async (rows) => { await save(2, { qa: progressFromDrafts(rows) }); }, setSavingQa);
  const next = () => run(async (rows) => { await save(STEP_AFTER_KNOWLEDGE, { qa: progressFromDrafts(rows) }); onNext(); }, setLeaving);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t(`${K}.heading`)}</h1>
        <p className="mt-1 text-fg-muted">{t(`${K}.subtitle`)}</p>
        {copy.knowledgeExamples ? <p className="mt-1 text-sm text-fg-subtle">{t(`${K}.examples`, { examples: copy.knowledgeExamples })}</p> : null}
      </div>
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} /> : null}
      <Tabs value={tab} onValueChange={(value) => { setTab(value as SourceTab); }} className="card">
        <TabsList className="px-3">
          <TabsTrigger value="files" className="inline-flex items-center gap-2">
            <Icon name="upload" className="size-4" />
            {t(`${K}.tabFiles`)}
          </TabsTrigger>
          <TabsTrigger value="crawl" className="inline-flex items-center gap-2">
            <Icon name="globe" className="size-4" />
            {t(`${K}.tabCrawl`)}
          </TabsTrigger>
          <TabsTrigger value="qa" className="inline-flex items-center gap-2">
            <Icon name="message-square" className="size-4" />
            {t(`${K}.tabQa`)}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="files" className="card-body">
          <FilesTab uploads={uploads} targets={targets} sensitive={copy.sensitive} />
        </TabsContent>
        <TabsContent value="crawl" className="card-body">
          {targets.ready ? <CrawlTab workspace={workspace} targets={targets} /> : <Skeleton className="h-40 w-full" />}
        </TabsContent>
        <TabsContent value="qa" className="card-body">
          <QaTab drafts={drafts} example={copy.starterQuestions[0]} problems={problems} saving={savingQa} onChange={setDrafts} onSave={() => void saveOnly()} />
        </TabsContent>
      </Tabs>
      <SourcesList sources={sources} uploads={uploads} onPick={setTab} />
      <p className="flex items-start gap-2 text-xs text-fg-subtle">
        <Icon name="info" className="mt-0.5 size-3.5 shrink-0" />
        {t(`${K}.moreLater`)}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5">
        <Button variant="ghost" onClick={onBack}>
          <Icon name="arrow-left" />
          {t("onboarding.back")}
        </Button>
        <div className="flex items-center gap-3">
          {uploads.busy ? <span className="text-xs text-fg-subtle" role="status">{t(`${K}.waitUploads`)}</span> : null}
          <Button loading={leaving} disabled={uploads.busy} onClick={() => void next()}>
            {t(`${K}.next`)}
            <Icon name="arrow-right" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Step 2: add knowledge — real file uploads, website crawl and Q&A pairs, as in the Knowledge Base. */
export function StepKnowledge(props: StepKnowledgeProps) {
  const { workspace } = props;
  if (!workspace) return <Skeleton className="h-72 w-full" />;
  return (
    <WorkspaceScope workspaceId={workspace.id} fallback={<Skeleton className="h-72 w-full" />}>
      <KnowledgeBody {...props} />
    </WorkspaceScope>
  );
}
